import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Download, Building2, ArrowRight } from 'lucide-react'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { reports as reportsApi } from '@/utils/api'
import { getMonthName } from '@/utils/formatters'
import type { AxiosResponse } from 'axios'

const COLORS = ['#1E4B8E', '#F7941D', '#10B981', '#6366F1', '#EC4899', '#8B5CF6', '#F59E0B', '#3B82F6']

interface ReportStats {
  monthlyHours?: { month: number; hours: number }[]
  monthlySickDays?: { month: number; days: number }[]
  ordersByProject?: { project: string; count: number }[]
}

export default function Berichte() {
  const { data: stats, isLoading } = useQuery<ReportStats>({
    queryKey: ['reports', 'stats'],
    queryFn: () => reportsApi.stats().then((r) => r.data),
  })
  const [reportParams, setReportParams] = useState({
    dailyDate: new Date().toISOString().split('T')[0],
    monthlyMonth: new Date().getMonth() + 1,
    monthlyYear: new Date().getFullYear(),
    vacationYear: new Date().getFullYear(),
  })

  const downloadBlob = (response: AxiosResponse, defaultFileName: string) => {
    const blob = new Blob([response.data], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const contentDisposition = response.headers['content-disposition']
    let fileName = defaultFileName
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) fileName = decodeURIComponent(match[1].replace(/['"]/g, ''))
    }
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const handleDownloadDaily = async () => {
    try {
      const response = await reportsApi.downloadDailyPdf(reportParams.dailyDate)
      downloadBlob(response, `Tagesbericht_${reportParams.dailyDate}.pdf`)
    } catch {
      toast.error('Fehler beim Herunterladen des Tagesberichts')
    }
  }

  const handleDownloadMonthly = async () => {
    try {
      const response = await reportsApi.downloadMonthlyPdf(reportParams.monthlyMonth, reportParams.monthlyYear)
      downloadBlob(response, `Monatsbericht_${reportParams.monthlyMonth}_${reportParams.monthlyYear}.pdf`)
    } catch {
      toast.error('Fehler beim Herunterladen des Monatsberichts')
    }
  }

  const handleDownloadVacation = async () => {
    try {
      const response = await reportsApi.downloadVacationPdf(reportParams.vacationYear)
      downloadBlob(response, `Urlaubsbericht_${reportParams.vacationYear}.pdf`)
    } catch {
      toast.error('Fehler beim Herunterladen des Urlaubsberichts')
    }
  }

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: getMonthName(i + 1) }))
  const yearOptions = Array.from({ length: 5 }, (_, i) => ({ value: new Date().getFullYear() - i, label: String(new Date().getFullYear() - i) }))

  const monthlyHoursData = stats?.monthlyHours?.map((m) => ({ name: getMonthName(m.month, true), stunden: m.hours })) || []
  const monthlySickData = stats?.monthlySickDays?.map((m) => ({ name: getMonthName(m.month, true), tage: m.days })) || []
  const projectOrdersData = stats?.ordersByProject?.slice(0, 8) || []

  if (isLoading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 w-full" /></div>
  }

  return (
    <div>
      <PageHeader title="Berichte" description="Generieren Sie Berichte und sehen Sie Statistiken" />

      {/* Quick Link */}
      <Link
        to="/admin/berichte/bauvorhaben"
        className="mb-6 block rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 p-4 text-white transition-colors hover:from-primary-600 hover:to-primary-700"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Building2 className="mr-4 h-8 w-8" />
            <div>
              <h3 className="text-lg font-semibold">Bauvorhaben-Statistiken</h3>
              <p className="text-sm text-primary-100">Detaillierte Auswertungen pro Bauvorhaben</p>
            </div>
          </div>
          <ArrowRight className="h-6 w-6" />
        </div>
      </Link>

      {/* Report Downloads */}
      <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card>
          <CardHeader><CardTitle>Tagesbericht</CardTitle></CardHeader>
          <CardContent>
            <Input
              type="date"
              value={reportParams.dailyDate}
              onChange={(e) => setReportParams({ ...reportParams, dailyDate: e.target.value })}
              className="mb-4"
            />
            <Button onClick={handleDownloadDaily} className="w-full">
              <Download className="h-4 w-4" /> PDF herunterladen
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Monatsbericht Zeiterfassung</CardTitle></CardHeader>
          <CardContent>
            <div className="mb-4 grid grid-cols-2 gap-2">
              <select
                value={reportParams.monthlyMonth}
                onChange={(e) => setReportParams({ ...reportParams, monthlyMonth: parseInt(e.target.value) })}
                className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm"
              >
                {monthOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select
                value={reportParams.monthlyYear}
                onChange={(e) => setReportParams({ ...reportParams, monthlyYear: parseInt(e.target.value) })}
                className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm"
              >
                {yearOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <Button onClick={handleDownloadMonthly} className="w-full">
              <Download className="h-4 w-4" /> PDF herunterladen
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Jahresuebersicht Urlaub</CardTitle></CardHeader>
          <CardContent>
            <select
              value={reportParams.vacationYear}
              onChange={(e) => setReportParams({ ...reportParams, vacationYear: parseInt(e.target.value) })}
              className="mb-4 flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm"
            >
              {yearOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <Button onClick={handleDownloadVacation} className="w-full">
              <Download className="h-4 w-4" /> PDF herunterladen
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Arbeitsstunden pro Monat</CardTitle></CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyHoursData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="stunden" fill="#1E4B8E" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Kranktage pro Monat</CardTitle></CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlySickData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="tage" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Bestellungen pro Bauvorhaben</CardTitle></CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={projectOrdersData}
                    cx="50%" cy="50%"
                    labelLine={false}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="count"
                    nameKey="project"
                    label={({ project, percent }: { project: string; percent: number }) =>
                      `${project.substring(0, 15)}... (${(percent * 100).toFixed(0)}%)`
                    }
                  >
                    {projectOrdersData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => [value, 'Bestellungen']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
